/**
 * Chevrotain Lexer for the Math Exercise DSL
 */
import { Lexer, IToken, ILexingError } from 'chevrotain';
import { allTokens } from './tokens';
import { LexerError } from '../errors';

/**
 * The lexer instance - reusable across all tokenize calls
 */
export const MathExerciseLexer = new Lexer(allTokens, {
  ensureOptimizations: false,  // Can't optimize FreeText token (it uses complement sets)
  positionTracking: 'full'
});

/**
 * Result of tokenization
 */
export interface TokenizeResult {
  tokens: IToken[];
  errors: ILexingError[];
}

/**
 * Tokenize a DSL input string
 *
 * Non-breaking spaces (U+00A0) copied from Slack/Teams are replaced with
 * regular spaces before tokenization to avoid silent parse failures.
 *
 * @param input - The DSL text to tokenize
 * @returns Array of tokens
 * @throws LexerError if tokenization fails
 */
export function tokenize(input: string): IToken[] {
  const result = MathExerciseLexer.tokenize(input.replace(/\u00A0/g, ' '));

  if (result.errors.length > 0) {
    throw new LexerError(result.errors);
  }

  return result.tokens;
}

/**
 * Tokenize without throwing - returns both tokens and errors
 * Useful for error-tolerant parsing (e.g., in editors)
 *
 * Non-breaking spaces (U+00A0) are sanitized the same way as in tokenize().
 */
export function tokenizeSafe(input: string): TokenizeResult {
  const result = MathExerciseLexer.tokenize(input.replace(/\u00A0/g, ' '));
  return {
    tokens: result.tokens,
    errors: result.errors
  };
}
